<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../functionCollection';
    import Swal from 'sweetalert2';
    import axios from 'axios';

    const props = defineProps(['fetchUrl', 'deleteTarifUrl', 'updateTarifUrl', 'showTarifUrl']);

    const tarifList = ref(null);
    const columnsToFetch = "id,tarif,name,price,group,created_at,updated_at";
    const idToChange = ref(null);

    // for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const tarifsOnPage = ref(null);
    const footerPageNumbers = ref(new Array());

    // initial data fetch and footer construction
    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        tarifList.value = await getFromAPI(url);
        maxPageNumber.value = tarifList.value.meta.last_page;
        tarifList.value = tarifList.value.data;
        tarifsOnPage.value = tarifList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    });

    // fills in the URL used for fetching with the needed URL parameters.
    function getFullUrl(url) {
        return url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}&column=id&order=asc`);
    }

    function replaceIDinUrl(url, id) {
        let formattedUrl = url.replace(':id', id);
        return formattedUrl;
    }

    // calls new tarifList whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        tarifList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = tarifList.value.meta.last_page;
        tarifList.value = tarifList.value.data;
        tarifsOnPage.value = tarifList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    // asks for confirmation if a tarif should be deleted. calls a SwiftAlert2 modal.
    // When successful, refetch the current page of the list, which should remove the deleted tarif.
    function deleteTarif(id) {
        Swal.fire({
            title: "Sind Sie sicher?",
            text: "Sind Sie sicher, dass Sie den ausgewählten Tarif löschen möchten?",
            icon: "warning",
            confirmButtonText: "Tarif löschen",
            confirmButtonColor: "#e64942",
            showCancelButton: true,
            cancelButtonText: "Abbrechen",
            focusCancel: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {

                axios.delete(replaceIDinUrl(props.deleteTarifUrl, id))
                .then((data) => {
                    if (data.status === 200) {
                        Swal.fire({
                            title: "Erfolgreich",
                            text: "Tarif wurde gelöscht!",
                            icon: "success",
                        }).then((result) => {
                            if (result.isConfirmed === true) {
                                getNewPage(currentPage.value);
                                // window.location.reload();
                            }
                        })
                    }
                }).catch((error) => {
                    Swal.fire({
                        title: "Löschen fehlgeschlagen!",
                        text: error.response.data.message,
                        icon: 'error',
                    })
                })
            }
        })
    }

</script>
<template>
    <div class="main-table" v-if="tarifList && tarifList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">Tarifnummer</th>
                        <th class="table-head">Name</th>
                        <th class="table-head">Preis</th>
                        <th class="table-head">Tarifgruppe</th>
                        <th class="table-head">Erstellt</th>
                        <th class="table-head">Aktualisiert</th>
                        <th class="table-head">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="tarif of tarifList" :key="tarif.id">
                        <td>{{ tarif.id }}</td>
                        <td>{{ tarif.tarif }}</td>
                        <td>{{ tarif.name }}</td>
                        <td>{{ tarif.price }}</td>
                        <td>{{ tarif.group }}</td>
                        <td>{{ tarif.created_at }}</td>
                        <td>{{ tarif.updated_at }}</td>
                        <td>
                            <div class="buttons">
                                <button type="button" class="btn btn-primary" @click.prevent="deleteTarif(tarif.id)">
                                    Löschen
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    @click.prevent="idToChange = tarif.id"
                                    data-bs-toggle="modal"
                                    data-bs-target="#changeTarif"
                                >
                                    Bearbeiten
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="tarifList && tarifList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="tarifList && (tarifList.length == 0 || tarifList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine Tarife gefunden.</span>
        </div>
    </div>

    <div class="modal fade" tabindex="-1" role="dialog" id="changeTarif">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Tarif ändern</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                    <parkobject-tarif-add :save-url="replaceIDinUrl(props.updateTarifUrl, idToChange)" state="edit" :fetchUrl="replaceIDinUrl(props.showTarifUrl, idToChange)"/>
                </div>
            </div>
        </div>
    </div>

</template>
